/**
 * [비즈니스 목적]
 * 경영정보 파일 온톨로지 메타데이터 및 프롬프트 관리 페이지
 *
 * [워크플로우]
 * 1. 테넌트 선택 → 파일 목록 및 프롬프트 연결 정보 로드
 * 2. 파일 기본 정보 관리 (파일명, 키워드, 버킷 경로 등)
 * 3. 파일-프롬프트 N:N 연결 관리
 *    - 기존 프롬프트 연결
 *    - 새 프롬프트 생성 및 연결
 *    - 프롬프트 편집
 *    - 프롬프트 연결 해제
 *
 * [주요 기능]
 * 1. 파일 온톨로지 CRUD
 *    - 생성: 새 파일 메타데이터 등록
 *    - 조회: 테넌트별 파일 목록 표시
 *    - 수정: 파일 정보 편집
 *    - 삭제: 파일 메타데이터 삭제
 *    - 복사: 기존 파일 메타데이터 복제 (타임스탬프 추가)
 *
 * 2. 파일-프롬프트 N:N 관계 관리
 *    - 한 파일에 여러 프롬프트 연결 가능
 *    - 프롬프트 재사용 (여러 파일이 같은 프롬프트 공유)
 *    - 프롬프트별 시트 필터링 설정 (reference_sheet_names, exclude_sheet_keywords)
 *
 * 3. 파일 선택 로직 설정
 *    - 1단계: 수집 키워드 매칭 (OR 조건)
 *    - 2단계: 제외 키워드 필터링
 *
 * [데이터 구조]
 * - file_ontology: 파일 메타데이터 (file_id, tenant_code, 키워드, 버킷 경로 등)
 * - prompts: 재사용 가능한 프롬프트 마스터
 * - file_prompt: 파일-프롬프트 N:N 연결 테이블
 *
 * [UI 구조]
 * - 상단: 통계 카드 (전체 파일, 프롬프트 설정됨, 미설정 파일)
 * - 메인: 파일 목록 테이블 (연결된 프롬프트 표시)
 * - 다이얼로그: 2개 탭
 *   - Tab 0: 파일 기본 정보
 *   - Tab 1: 프롬프트별 데이터 정보 (N:N 관계)
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Switch,
  FormControlLabel,
  Grid,
  Card,
  CardContent,
  Tabs,
  Tab,
  Tooltip,
  Autocomplete,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Description as DescriptionIcon,
  ContentCopy as CopyIcon,
  ExpandMore as ExpandMoreIcon,
  Code as CodeIcon
} from '@mui/icons-material';

import { useGlobalTenant } from '../../contexts/GlobalTenantContext';
import apiService from '../../services/api';
import DeleteConfirmDialog from './DeleteConfirmDialog';

const OntologyPage = () => {
  const [files, setFiles] = useState([]);
  const [filePrompts, setFilePrompts] = useState([]);
  const [prompts, setPrompts] = useState([]); // 재사용 가능한 프롬프트 목록
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [fileToDelete, setFileToDelete] = useState(null);
  const [selectedPrompt, setSelectedPrompt] = useState(null); // 선택된 프롬프트
  const [linkedPrompts, setLinkedPrompts] = useState([]); // 현재 파일에 연결된 프롬프트 목록 (N:N)
  const [isCreatingNewPrompt, setIsCreatingNewPrompt] = useState(false); // 새 프롬프트 생성 모드
  const [editingPrompt, setEditingPrompt] = useState(null); // 편집 중인 프롬프트
  const [editDialogOpen, setEditDialogOpen] = useState(false); // 편집 다이얼로그 열림 상태

  // Form state - 파일 정보와 프롬프트 정보 통합
  const [formData, setFormData] = useState({
    // 파일 식별 정보
    tenant_code: '',
    file_id: '',
    file_extension: '.xlsx',
    // 파일 정보
    file_name: '',
    description: '',
    collection_keywords: '',
    collection_exclude_keywords: '',
    // 소스 정보
    original_bucket_path: '',
    original_bucket_paths: [],  // 다중 버킷 경로 (배열)
    converted_bucket_path: '',
    // 프롬프트 정보
    prompt_id: 'default',
    prompt_name: '',
    convert_prompt: '',
    current_prompt: '',
    is_active: true,
    // 시트 필터링 정보 (NEW)
    referenceSheetNames: '',
    excludeSheetKeywords: ''
  });

  const { selectedTenant, getSelectedTenantInfo, tenants, setSelectedTenant } = useGlobalTenant();

  // 파일 목록과 프롬프트 조회
  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      // 선택된 테넌트의 tenant_code 추출
      const tenantInfo = getSelectedTenantInfo();
      if (!tenantInfo) {
        console.warn('⚠️ 테넌트가 선택되지 않았습니다');
        setLoading(false);
        return;
      }
      const tenantCode = tenantInfo.tenant_code;

      console.log('🔍 fetchData 호출됨, tenantCode:', tenantCode);

      // 1. file_ontology에서 파일 목록 가져오기
      const filesResponse = await apiService.getFiles(tenantCode);
      console.log('📁 파일 목록 응답:', filesResponse);

      if (filesResponse.success) {
        // tenant_name이 없는 파일에 tenants 정보에서 매핑
        const filesWithTenantName = filesResponse.data.map(file => {
          if (file.tenant_name) return file;

          // tenants 배열에서 tenant_code로 tenant_name 찾기
          const matchingTenant = tenants.find(t =>
            t.tenant_codes?.some(tc => tc.tenant_code === file.tenant_code)
          );

          return {
            ...file,
            tenant_name: matchingTenant?.tenant_name || null
          };
        });

        setFiles(filesWithTenantName);
        console.log(`✅ 파일 ${filesWithTenantName.length}개 로드됨 (tenant_name 매핑 완료)`);
      }

      // 2. file_prompt 목록 가져오기
      const promptsResponse = await apiService.getFilePrompts(tenantCode);
      console.log('🔗 파일 프롬프트 연결 응답:', promptsResponse);

      if (promptsResponse.success) {
        setFilePrompts(promptsResponse.data);
        console.log(`✅ 파일 프롬프트 연결 ${promptsResponse.data.length}개 로드됨`);
      }

      // 3. prompts 마스터 목록 가져오기 (재사용 가능한 프롬프트)
      const promptsMasterResponse = await apiService.getPrompts(tenantCode);
      console.log('📋 프롬프트 마스터 응답:', promptsMasterResponse);

      if (promptsMasterResponse.success) {
        setPrompts(promptsMasterResponse.data);
        console.log(`✅ 프롬프트 마스터 ${promptsMasterResponse.data.length}개 로드됨`);
      }
    } catch (error) {
      console.error('❌ Error fetching data:', error);
      setError(error.message || '데이터를 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [selectedTenant]);

  // 프롬프트 설정 다이얼로그 열기
  const handleOpenDialog = (file) => {
    setSelectedFile(file);

    // 해당 파일의 모든 연결된 프롬프트 찾기 (N:N 관계)
    const fileLinkedPrompts = filePrompts.filter(
      p => p.tenant_code === file.tenant_code && p.file_id === file.file_id
    );
    setLinkedPrompts(fileLinkedPrompts);

    // 파일 정보만 설정 (프롬프트 정보는 linkedPrompts 상태로 분리)
    setFormData({
      // 파일 식별 정보
      tenant_code: file.tenant_code || '',
      file_id: file.file_id || '',
      file_extension: file.file_extension || '.xlsx',
      // 파일 정보 (file_ontology에서)
      standard_file_name: file.standard_file_name || '',
      description: file.description || '',
      collection_keywords: file.collection_keywords || '',
      collection_exclude_keywords: file.collection_exclude_keywords || '',
      // 소스 정보 — Postgres String[] 가 빈 배열로 올 수도 있어 length 검사 후 single column 으로 fallback.
      original_bucket_path: file.original_bucket_path || '',
      original_bucket_paths: (Array.isArray(file.original_bucket_paths) && file.original_bucket_paths.length > 0)
        ? file.original_bucket_paths
        : (file.original_bucket_path ? [file.original_bucket_path] : []),
      converted_bucket_path: file.converted_bucket_path || '',
      // 프롬프트 정보는 이제 linkedPrompts 배열로 관리
      prompt_id: '',
      convert_prompt: '',
      current_prompt: '',
      is_active: true
    });

    setOpenDialog(true);
    setError(null);
    setSuccess(null);
  };

  // 다이얼로그 닫기
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedFile(null);
    setLinkedPrompts([]); // 연결된 프롬프트 목록 초기화
    setIsCreatingNewPrompt(false); // 새 프롬프트 생성 모드 초기화
    setError(null);
    setSuccess(null);
    setTabValue(0); // 탭 초기화
  };

  // 새 파일 추가 다이얼로그 열기
  const handleAddNewFile = () => {
    // 현재 선택된 테넌트의 코드 가져오기
    const tenantInfo = getSelectedTenantInfo();
    const tenantCode = tenantInfo?.tenant_codes?.find(tc => tc.is_primary)?.tenant_code
                      || tenantInfo?.tenant_codes?.[0]?.tenant_code
                      || '';

    if (!tenantCode) {
      setError('테넌트를 먼저 선택해주세요.');
      return;
    }

    setSelectedFile(null); // 새 파일 생성 모드
    setLinkedPrompts([]); // 연결된 프롬프트 초기화

    // 빈 폼 데이터로 초기화
    setFormData({
      // 파일 식별 정보
      tenant_code: tenantCode,
      file_id: '',
      file_extension: '.xlsx',
      // 파일 정보
      standard_file_name: '',
      description: '',
      collection_keywords: '',
      collection_exclude_keywords: '',
      // 소스 정보
      original_bucket_path: `hyperlounge-${tenantCode}-foldersync/shared_drive/`,
      original_bucket_paths: [],
      converted_bucket_path: `hyperlounge-${tenantCode}-foldersync/converted/`,
      // 프롬프트 정보
      prompt_id: '',
      convert_prompt: '',
      current_prompt: '',
      is_active: true
    });

    setOpenDialog(true);
    setError(null);
    setSuccess(null);
  };

  // 프롬프트 추가 핸들러 (N:N 관계)
  const handleAddPrompt = async (event, value) => {
    if (!value || !selectedFile) return;

    setError(null);
    setSuccess(null);

    try {
      // "새 프롬프트 만들기" 선택 시 - 폼 초기화
      if (value.isNew || value.prompt_id === '__NEW__') {
        setIsCreatingNewPrompt(true); // 새 프롬프트 생성 모드 활성화
        setSelectedPrompt(null);
        setFormData(prev => ({
          ...prev,
          prompt_id: '',
          prompt_name: '',
          convert_prompt: '',
          current_prompt: ''
        }));
        return;
      }

      // 이미 연결된 프롬프트인지 확인
      const alreadyLinked = linkedPrompts.some(
        p => p.prompt_id === value.prompt_id
      );

      if (alreadyLinked) {
        setError(`프롬프트 "${value.prompt_id}"는 이미 이 파일에 연결되어 있습니다.`);
        return;
      }

      // 기존 프롬프트 선택 - 프롬프트 데이터 로드
      const tenantCodeToUse = value.tenant_code || formData.tenant_code;
      if (!tenantCodeToUse) {
        setError('테넌트 정보를 찾을 수 없습니다.');
        return;
      }

      const response = await apiService.getPrompt(tenantCodeToUse, value.prompt_id);
      if (!response.success) {
        setError('프롬프트를 불러오는데 실패했습니다.');
        return;
      }

      const promptData = response.data;

      // file_prompt 테이블에 연결 생성
      const linkData = {
        tenant_code: selectedFile.tenant_code,
        file_id: selectedFile.file_id,
        prompt_id: promptData.prompt_id,
        prompt_name: promptData.prompt_name,
        convert_prompt: promptData.convert_prompt,
        current_prompt: promptData.current_prompt,
        is_active: true,
        created_by: 'user'
      };

      const linkResponse = await apiService.createFilePrompt(linkData);

      if (linkResponse.success) {
        setSuccess(`프롬프트 "${promptData.prompt_id}"가 추가되었습니다.`);

        // 연결된 프롬프트 목록 업데이트
        setLinkedPrompts(prev => [...prev, linkResponse.data.link]);

        // 전역 데이터 새로고침
        fetchData();

        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError('프롬프트 연결에 실패했습니다.');
      }
    } catch (error) {
      console.error('Error adding prompt:', error);
      setError('프롬프트를 추가하는데 실패했습니다: ' + error.message);
    }
  };

  // 프롬프트 연결 해제 핸들러 (N:N 관계)
  const handleRemovePrompt = async (promptId) => {
    if (!selectedFile || !promptId) return;

    setError(null);
    setSuccess(null);

    try {
      const response = await apiService.deleteFilePrompt(
        selectedFile.tenant_code,
        selectedFile.file_id,
        promptId
      );

      if (response.success) {
        setSuccess(`프롬프트 "${promptId}"의 연결이 해제되었습니다.`);

        // 연결된 프롬프트 목록에서 제거
        setLinkedPrompts(prev => prev.filter(p => p.prompt_id !== promptId));

        // 전역 데이터 새로고침
        fetchData();

        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError('프롬프트 연결 해제에 실패했습니다.');
      }
    } catch (error) {
      console.error('Error removing prompt:', error);
      setError('프롬프트 연결 해제에 실패했습니다: ' + error.message);
    }
  };

  // 프롬프트 편집 다이얼로그 열기
  const handleEditPrompt = async (prompt) => {
    setError(null);
    setSuccess(null);

    try {
      // prompts 테이블에서 전체 데이터 로드
      const response = await apiService.getPrompt(prompt.tenant_code, prompt.prompt_id);

      if (response.success) {
        const fullPromptData = response.data;
        setEditingPrompt({
          ...fullPromptData,
          // 시트 필터링: 배열을 쉼표로 구분된 문자열로 변환
          referenceSheetNames: fullPromptData.reference_sheet_names
            ? (Array.isArray(fullPromptData.reference_sheet_names)
                ? fullPromptData.reference_sheet_names.join(', ')
                : fullPromptData.reference_sheet_names)
            : '',
          excludeSheetKeywords: fullPromptData.exclude_sheet_keywords
            ? (Array.isArray(fullPromptData.exclude_sheet_keywords)
                ? fullPromptData.exclude_sheet_keywords.join(', ')
                : fullPromptData.exclude_sheet_keywords)
            : ''
        });
        setEditDialogOpen(true);
      } else {
        setError('프롬프트를 불러오는데 실패했습니다.');
      }
    } catch (error) {
      console.error('Error loading prompt for edit:', error);
      setError('프롬프트를 불러오는데 실패했습니다: ' + error.message);
    }
  };

  // 프롬프트 편집 저장
  const handleSaveEditedPrompt = async () => {
    if (!editingPrompt) return;

    setError(null);
    setSuccess(null);

    try {
      // 시트 필터링: 쉼표로 구분된 문자열을 배열로 변환
      const referenceSheetNames = editingPrompt.referenceSheetNames
        ? editingPrompt.referenceSheetNames.split(',').map(s => s.trim()).filter(s => s.length > 0)
        : [];
      const excludeSheetKeywords = editingPrompt.excludeSheetKeywords
        ? editingPrompt.excludeSheetKeywords.split(',').map(s => s.trim()).filter(s => s.length > 0)
        : [];

      const updateData = {
        prompt_name: editingPrompt.prompt_name,
        convert_prompt: editingPrompt.convert_prompt,
        current_prompt: editingPrompt.current_prompt,
        is_active: editingPrompt.is_active,
        reference_sheet_names: referenceSheetNames,
        exclude_sheet_keywords: excludeSheetKeywords,
        updated_by: 'user'
      };

      const response = await apiService.updatePrompt(
        editingPrompt.tenant_code,
        editingPrompt.prompt_id,
        updateData
      );

      if (response.success) {
        setSuccess('프롬프트가 수정되었습니다.');

        // 연결된 프롬프트 목록 업데이트
        setLinkedPrompts(prev => prev.map(p =>
          p.prompt_id === editingPrompt.prompt_id
            ? { ...p, ...response.data }
            : p
        ));

        // 전역 데이터 새로고침
        fetchData();

        setTimeout(() => {
          setEditDialogOpen(false);
          setEditingPrompt(null);
          setSuccess(null);
        }, 1500);
      } else {
        setError('프롬프트 수정에 실패했습니다: ' + (response.error || '알 수 없는 오류'));
      }
    } catch (error) {
      console.error('Error updating prompt:', error);
      setError('프롬프트 수정에 실패했습니다: ' + error.message);
    }
  };

  // 편집 다이얼로그 닫기
  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setEditingPrompt(null);
    setError(null);
    setSuccess(null);
  };

  // 폼 필드 변경 핸들러
  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // 프롬프트 선택 핸들러
  const handlePromptSelect = async (event, value) => {
    if (!value) {
      // 선택 해제
      setSelectedPrompt(null);
      setFormData(prev => ({
        ...prev,
        prompt_id: '',
        convert_prompt: '',
        current_prompt: ''
      }));
      return;
    }

    // "새 프롬프트 만들기" 선택
    if (value.isNew || value.prompt_id === '__NEW__') {
      setSelectedPrompt(null);
      setFormData(prev => ({
        ...prev,
        prompt_id: '',
        convert_prompt: '',
        current_prompt: ''
      }));
      return;
    }

    // 기존 프롬프트 선택
    try {
      // value 객체에서 tenant_code를 가져옴 (프롬프트는 이미 tenant_code를 포함하고 있음)
      const tenantCodeToUse = value.tenant_code || formData.tenant_code;

      if (!tenantCodeToUse) {
        setError('테넌트 정보를 찾을 수 없습니다.');
        return;
      }

      const response = await apiService.getPrompt(tenantCodeToUse, value.prompt_id);
      if (response.success) {
        const promptData = response.data;
        setSelectedPrompt(promptData);
        setFormData(prev => ({
          ...prev,
          prompt_id: promptData.prompt_id || '',
          convert_prompt: promptData.convert_prompt || '',
          current_prompt: promptData.current_prompt || '',
          // 시트 필터링: 배열을 쉼표로 구분된 문자열로 변환
          referenceSheetNames: promptData.reference_sheet_names ? promptData.reference_sheet_names.join(', ') : '',
          excludeSheetKeywords: promptData.exclude_sheet_keywords ? promptData.exclude_sheet_keywords.join(', ') : ''
        }));
      }
    } catch (error) {
      console.error('Error loading prompt:', error);
      setError('프롬프트를 불러오는데 실패했습니다.');
    }
  };

  // 저장 (N:N 관계 지원)
  const handleSave = async () => {
    setError(null);
    setSuccess(null);

    try {
      // 선택된 테넌트의 tenant_code 추출
      const tenantInfo = getSelectedTenantInfo();
      if (!tenantInfo) {
        setError('테넌트가 선택되지 않았습니다.');
        return;
      }
      const currentTenantCode = tenantInfo.tenant_code;

      // 1. 파일 정보 저장 (file_ontology 테이블)
      const fileData = {
        tenant_code: selectedFile?.tenant_code || currentTenantCode,
        file_id: selectedFile?.file_id || formData.file_id,
        file_extension: formData.file_extension,
        standard_file_name: formData.standard_file_name,
        description: formData.description,
        collection_keywords: formData.collection_keywords,
        collection_exclude_keywords: formData.collection_exclude_keywords,
        original_bucket_path: formData.original_bucket_path,
        original_bucket_paths: formData.original_bucket_paths || [],  // 다중 버킷 경로
        converted_bucket_path: formData.converted_bucket_path
      };

      let fileResponse;

      // selectedFile이 있으면 수정, 없으면 생성
      if (selectedFile) {
        // 수정 모드
        fileResponse = await apiService.updateFile(selectedFile.id, fileData);
      } else {
        // 생성 모드
        fileResponse = await apiService.createFile(fileData);
      }

      if (!fileResponse.success) {
        setError('파일 정보 저장 실패: ' + (fileResponse.error || '알 수 없는 오류'));
        return;
      }

      const savedFile = selectedFile || fileResponse.data;

      // 2. 새 프롬프트 생성 및 연결 (formData.prompt_id가 있는 경우만)
      if (formData.prompt_id) {
        // 시트 필터링: 쉼표로 구분된 문자열을 배열로 변환
        const referenceSheetNames = formData.referenceSheetNames
          ? formData.referenceSheetNames.split(',').map(s => s.trim()).filter(s => s.length > 0)
          : null;
        const excludeSheetKeywords = formData.excludeSheetKeywords
          ? formData.excludeSheetKeywords.split(',').map(s => s.trim()).filter(s => s.length > 0)
          : null;

        const promptData = {
          prompt_id: formData.prompt_id,
          prompt_name: formData.prompt_name || formData.prompt_id, // 사용자가 입력한 프롬프트 이름 사용
          tenant_code: savedFile.tenant_code,
          file_id: savedFile.file_id,
          convert_prompt: formData.convert_prompt,
          current_prompt: formData.current_prompt,
          is_active: formData.is_active !== false,
          created_by: 'user',
          // 시트 필터링 (NEW)
          referenceSheetNames: referenceSheetNames,
          excludeSheetKeywords: excludeSheetKeywords
        };

        const promptResponse = await apiService.createFilePrompt(promptData);

        if (promptResponse.success) {
          setSuccess('파일 정보가 저장되고 프롬프트가 추가되었습니다.');

          // 연결된 프롬프트 목록 업데이트
          setLinkedPrompts(prev => [...prev, promptResponse.data.link]);

          // 폼 초기화 (새 프롬프트 추가 준비)
          setFormData(prev => ({
            ...prev,
            prompt_id: '',
            prompt_name: '',
            convert_prompt: '',
            current_prompt: ''
          }));
          setIsCreatingNewPrompt(false); // 새 프롬프트 생성 모드 초기화
        } else {
          setError('파일은 저장되었으나 프롬프트 추가 실패: ' + (promptResponse.error || '알 수 없는 오류'));
        }
      } else {
        // 프롬프트 정보 없이 파일만 저장
        setSuccess(selectedFile ? '파일 정보가 수정되었습니다.' : '파일이 생성되었습니다.');
      }

      fetchData();

      // 성공 메시지 표시 후 다이얼로그 닫기
      setTimeout(() => {
        if (!formData.prompt_id) {
          handleCloseDialog();
        }
      }, 1500);

    } catch (error) {
      console.error('Error saving:', error);
      setError(error.message || '저장에 실패했습니다.');
    }
  };

  // 프롬프트 연결 해제 (파일에서만 제거, 프롬프트는 유지)
  const handleDeletePrompt = async (promptId) => {
    if (!window.confirm('이 파일에서 프롬프트 연결을 해제하시겠습니까?\n\n※ 프롬프트 자체는 삭제되지 않으며, 다른 파일에서 계속 사용할 수 있습니다.\n※ 프롬프트를 완전히 삭제하려면 "프롬프트 관리" 메뉴를 이용하세요.')) {
      return;
    }

    try {
      const response = await apiService.deleteFilePrompt(promptId);
      if (response.success) {
        setSuccess('프롬프트 연결이 해제되었습니다. (프롬프트는 삭제되지 않았습니다)');

        // 연결된 프롬프트 목록에서 제거
        setLinkedPrompts(prev => prev.filter(p => p.prompt_id !== promptId));

        fetchData();
      } else {
        setError(response.error || '연결 해제에 실패했습니다.');
      }
    } catch (error) {
      console.error('Error unlinking file prompt:', error);
      setError(error.message || '연결 해제에 실패했습니다.');
    }
  };

  // 복사 핸들러
  const handleCopy = (file) => {
    console.log('📋 파일 복사:', file);

    // 타임스탬프 생성 (YYYYMMDD_HHMMSS 형식)
    const timestamp = new Date().toISOString()
      .replace(/[-:T]/g, '')
      .replace(/\.\d{3}Z$/, '')
      .slice(0, 15); // YYYYMMDDHHMMSS

    // 기존 _COPY_* 패턴 제거 후 새로 추가 (중복 완전 방지)
    const baseFileId = file.file_id?.replace(/_COPY(_\d+)?$/g, '') || '';
    const baseFileName = file.standard_file_name?.replace(/ \(복사본 \d+\)$/g, '').replace(/ \(복사본\)$/g, '') || '';

    // 복사된 파일 데이터 생성 (ID 제거하여 새 파일로 생성)
    const copiedFile = {
      ...file,
      id: undefined, // ID 제거 (새로 생성됨)
      file_id: `${baseFileId}_COPY_${timestamp}`, // FILE_ID에 _COPY_타임스탬프 추가
      standard_file_name: `${baseFileName} (복사본 ${timestamp})`, // 파일명에 (복사본 타임스탬프) 추가
      is_active: true,
      created_at: new Date(),
      updated_at: new Date()
    };

    // 생성 모드로 설정 (selectedFile = null)
    setSelectedFile(null);

    // 복사된 파일 데이터로 폼 데이터 설정
    setFormData({
      // 파일 식별 정보
      tenant_code: copiedFile.tenant_code || '',
      file_id: copiedFile.file_id || '', // 새 FILE_ID (타임스탬프 포함)
      file_extension: copiedFile.file_extension || '.xlsx',
      // 파일 정보
      file_name: copiedFile.standard_file_name || '',
      description: copiedFile.description || '',
      collection_keywords: copiedFile.collection_keywords || '',
      collection_exclude_keywords: copiedFile.collection_exclude_keywords || '',
      // 소스 정보
      original_bucket_path: copiedFile.original_bucket_path || '',
      original_bucket_paths: (Array.isArray(copiedFile.original_bucket_paths) && copiedFile.original_bucket_paths.length > 0)
        ? copiedFile.original_bucket_paths
        : (copiedFile.original_bucket_path ? [copiedFile.original_bucket_path] : []),
      converted_bucket_path: copiedFile.converted_bucket_path || '',
      // 프롬프트 정보는 기본값으로 설정 (복사하지 않음)
      prompt_id: '',  // 빈 문자열로 설정하여 프롬프트 생성 건너뛰기
      prompt_name: '',
      convert_prompt: '',
      reference_sheet_names: '',
      exclude_sheet_keywords: '',
      is_active: true
    });

    // 다이얼로그 열기
    setOpenDialog(true);
    setError(null);
    setSuccess(null);
    setTabValue(0); // 첫 번째 탭으로 초기화
  };

  // 수정 핸들러 (기존 handleOpenDialog 재사용)
  const handleEdit = (file) => {
    handleOpenDialog(file);
  };

  // 삭제 핸들러
  const handleDeleteClick = (file) => {
    setFileToDelete(file);
    setDeleteDialogOpen(true);
  };

  // 삭제 확인
  const handleDeleteConfirm = async () => {
    try {
      if (fileToDelete) {
        await apiService.deleteFile(fileToDelete.id);
        setSuccess('파일이 삭제되었습니다.');
        setDeleteDialogOpen(false);
        setFileToDelete(null);
        fetchData();
      }
    } catch (error) {
      console.error('Error deleting file:', error);
      setError(error.message || '파일 삭제 중 오류가 발생했습니다.');
      setDeleteDialogOpen(false);
    }
  };

  // Alias for backward compatibility
  const handleDelete = handleDeleteClick;

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* 헤더 */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" component="h1">
            경영정보 파일 온톨로지 관리
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            경영정보 파일에 대한 메타데이터 및 프롬프트 설정 관리
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleAddNewFile}
          >
            파일 추가
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchData}
          >
            새로고침
          </Button>
        </Box>
      </Box>

      {/* 테넌트 선택 */}
      <Box sx={{ mb: 3 }}>
        <FormControl fullWidth size="small">
          <InputLabel>테넌트 선택</InputLabel>
          <Select
            value={selectedTenant}
            onChange={(e) => setSelectedTenant(e.target.value)}
            label="테넌트 선택"
          >
            {tenants.map((tenant) => (
              <MenuItem key={tenant.id} value={tenant.id}>
                {tenant.tenant_name} ({tenant.tenant_codes?.find(tc => tc.is_primary)?.tenant_code || tenant.tenant_codes?.[0]?.tenant_code})
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* 통계 카드 */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                전체 파일
              </Typography>
              <Typography variant="h4">
                {files.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                프롬프트 설정됨
              </Typography>
              <Typography variant="h4">
                {filePrompts.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                미설정 파일
              </Typography>
              <Typography variant="h4">
                {files.filter(file => !filePrompts.some(fp => fp.file_id === file.file_id && fp.tenant_code === file.tenant_code)).length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* 알림 메시지 */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* 테이블 */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>고객사</TableCell>
                <TableCell>표준 파일명</TableCell>
                <TableCell>연결된 프롬프트</TableCell>
                <TableCell>원본파일 경로</TableCell>
                <TableCell>변환파일 경로</TableCell>
                <TableCell>상태</TableCell>
                <TableCell align="center">작업</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {files.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center">
                    파일이 없습니다.
                  </TableCell>
                </TableRow>
              ) : (
                files.map((file) => {
                  // 해당 파일에 연결된 모든 프롬프트 찾기 (N:N)
                  const fileLinkedPrompts = filePrompts.filter(
                    p => p.tenant_code === file.tenant_code && p.file_id === file.file_id
                  );

                  // 디버깅: 첫 번째 파일의 프롬프트 연결 상태 출력
                  if (files.indexOf(file) === 0) {
                    console.log('🔍 첫 번째 파일의 프롬프트 연결 디버깅:');
                    console.log('  - file.tenant_code:', file.tenant_code);
                    console.log('  - file.file_id:', file.file_id);
                    console.log('  - filePrompts 전체:', filePrompts);
                    console.log('  - fileLinkedPrompts 필터 결과:', fileLinkedPrompts);
                  }

                  return (
                    <TableRow key={`${file.tenant_code}-${file.file_id}`}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <DescriptionIcon fontSize="small" color="action" />
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {file.tenant_name || '알 수 없음'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {file.tenant_code}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2">{file.standard_file_name || file.file_name}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            FILE_ID: {file.file_id || '-'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {fileLinkedPrompts.length > 0 ? (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            {fileLinkedPrompts.map((p, idx) => (
                              <Chip
                                key={p.prompt_id}
                                label={p.prompt_name || p.prompt_id}
                                size="small"
                                color={p.is_active === false ? 'default' : 'primary'}
                                variant={p.is_active === false ? 'outlined' : 'filled'}
                                sx={{
                                  maxWidth: '100%',
                                  height: 'auto',
                                  '& .MuiChip-label': {
                                    whiteSpace: 'normal',
                                    wordBreak: 'break-word'
                                  }
                                }}
                              />
                            ))}
                          </Box>
                        ) : (
                          <Chip label="미설정" size="small" color="default" variant="outlined" />
                        )}
                      </TableCell>
                      <TableCell>
                        <Tooltip title={
                          file.original_bucket_paths?.length > 0
                            ? file.original_bucket_paths.join('\n')
                            : file.original_bucket_path || '미설정'
                        }>
                          <Box>
                            {file.original_bucket_paths?.length > 0 ? (
                              <>
                                <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                                  {file.original_bucket_paths[0]}
                                </Typography>
                                {file.original_bucket_paths.length > 1 && (
                                  <Chip
                                    label={`+${file.original_bucket_paths.length - 1}개`}
                                    size="small"
                                    color="info"
                                    variant="outlined"
                                    sx={{ ml: 0.5, height: 18, fontSize: '0.7rem' }}
                                  />
                                )}
                              </>
                            ) : (
                              <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                                {file.original_bucket_path || '-'}
                              </Typography>
                            )}
                          </Box>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Tooltip title={file.converted_bucket_path || '미설정'}>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                            {file.converted_bucket_path || '-'}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={file.is_active ? '활성' : '비활성'}
                          size="small"
                          color={file.is_active ? 'success' : 'default'}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="복사">
                          <IconButton
                            size="small"
                            onClick={() => handleCopy(file)}
                            color="primary"
                          >
                            <CopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="수정">
                          <IconButton
                            size="small"
                            onClick={() => handleEdit(file)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="삭제">
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(file)}
                            color="error"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* 추가/수정 다이얼로그 */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedFile ? `프롬프트 설정: ${selectedFile.standard_file_name || selectedFile.file_id}` : `새 파일 생성: ${formData.standard_file_name || '프롬프트 설정'}`}
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          <Box sx={{ borderBottom: 1, borderColor: 'divider', mt: 2 }}>
            <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
              <Tab label="파일 기본정보" />
              <Tab label="프롬프트별 데이터 정보" />
            </Tabs>
          </Box>

          {/* Tab 0: 파일 기본정보 */}
          {tabValue === 0 && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="subtitle2" color="primary" gutterBottom>
                      파일 식별 정보
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      기본 정보만 입력하고 저장 후, 나중에 프롬프트별 세부 설정 가능
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              {/* 파일 기본정보 필드들 */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="표준 파일명 (standard_file_name)"
                  name="standard_file_name"
                  value={formData.standard_file_name || ''}
                  onChange={handleChange}
                  placeholder="예: (트리니티 반포점)통합 데일리 고객정보"
                  helperText="LLM이 참고할 표준 파일명 (파일 매칭에는 키워드가 우선 사용됩니다)"
                  required
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="FILE_ID (file_id)"
                  name="file_id"
                  value={formData.file_id || ''}
                  onChange={handleChange}
                  placeholder="예: CUSTOMER_INFO_BANPO"
                  helperText="파일 고유 식별자 (영문 대문자, 숫자, 언더스코어)"
                  required
                  disabled={selectedFile !== null}
                />
              </Grid>

              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>파일 확장자 (file_extension)</InputLabel>
                  <Select
                    name="file_extension"
                    value={formData.file_extension || '.xlsx'}
                    onChange={handleChange}
                    label="파일 확장자 (file_extension)"
                  >
                    <MenuItem value=".xlsx">.xlsx</MenuItem>
                    <MenuItem value=".xlsm">.xlsm</MenuItem>
                    <MenuItem value=".xlsb">.xlsb</MenuItem>
                    <MenuItem value=".xls">.xls</MenuItem>
                    <MenuItem value=".csv">.csv</MenuItem>
                    <MenuItem value=".json">.json</MenuItem>
                    <MenuItem value=".xml">.xml</MenuItem>
                    <MenuItem value=".txt">.txt</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <Autocomplete
                  multiple
                  freeSolo
                  options={[]}
                  value={formData.collection_keywords ? formData.collection_keywords.split(',').map(k => k.trim()).filter(k => k) : []}
                  onChange={(event, newValue) => {
                    setFormData(prev => ({
                      ...prev,
                      collection_keywords: newValue.join(', ')
                    }));
                  }}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        variant="outlined"
                        color="success"
                        label={option}
                        {...getTagProps({ index })}
                        key={index}
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="수집 키워드 (collection_keywords)"
                      placeholder="키워드 입력 후 Enter (예: 반포점, 고객정보)"
                      helperText="우선순위 1: 이 키워드들 중 하나라도 포함된 파일을 먼저 찾습니다 (OR 조건)"
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Autocomplete
                  multiple
                  freeSolo
                  options={[]}
                  value={formData.collection_exclude_keywords ? formData.collection_exclude_keywords.split(',').map(k => k.trim()).filter(k => k) : []}
                  onChange={(event, newValue) => {
                    setFormData(prev => ({
                      ...prev,
                      collection_exclude_keywords: newValue.join(', ')
                    }));
                  }}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        variant="outlined"
                        color="error"
                        label={option}
                        {...getTagProps({ index })}
                        key={index}
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="수집 제외 키워드 (collection_exclude_keywords)"
                      placeholder="제외할 키워드 입력 후 Enter (예: 테스트, 임시)"
                      helperText="우선순위 2: 수집 대상 중 이 키워드가 포함된 파일은 자동으로 제외합니다 (OR 조건)"
                    />
                  )}
                />
              </Grid>

              {/* 파일 선택 로직 설명 박스 */}
              <Grid item xs={12}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    bgcolor: 'success.lighter',
                    border: '1px solid',
                    borderColor: 'success.main',
                    borderRadius: 1
                  }}
                >
                  <Typography variant="subtitle2" color="success.main" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <DescriptionIcon fontSize="small" />
                    파일 선택 로직 (실행 순서)
                  </Typography>
                  <Typography variant="body2" component="div" sx={{ mt: 1, ml: 3 }}>
                    <Box component="ol" sx={{ pl: 2, m: 0 }}>
                      <li><strong>1단계 - 키워드 포함:</strong> 수집 키워드 중 하나라도 포함된 파일 필터링</li>
                      <li><strong>2단계 - 키워드 제외:</strong> 제외 키워드가 포함된 파일은 목록에서 제거</li>
                    </Box>
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', ml: 3 }}>
                    💡 <strong>키워드 우선:</strong> 파일명 변형(공백, 언더스코어, 특수문자)에 강건하게 대응하기 위해 키워드 매칭을 우선 사용합니다.
                    <br />
                    💡 <strong>OR 조건:</strong> 여러 키워드를 입력하면 하나만 매칭되어도 선택됩니다.
                    <br />
                    💡 <strong>예시:</strong> "반포점, 고객정보"를 입력하면 → "(트리니티_반포점) 통합 데일리 고객 정보_20250930.xlsx" 매칭 성공
                  </Typography>
                </Paper>
              </Grid>

              {/* 소스 정보 섹션 */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="primary" gutterBottom sx={{ mt: 2 }}>
                  소스 정보
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Autocomplete
                  multiple
                  freeSolo
                  options={[]}
                  value={formData.original_bucket_paths || []}
                  onChange={(event, newValue) => {
                    setFormData(prev => ({
                      ...prev,
                      original_bucket_paths: newValue,
                      // 첫 번째 경로를 original_bucket_path에도 저장 (하위 호환성)
                      original_bucket_path: newValue.length > 0 ? newValue[0] : ''
                    }));
                  }}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        variant="outlined"
                        color="info"
                        label={option}
                        {...getTagProps({ index })}
                        key={index}
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="원본 파일 버킷 경로들 (original_bucket_paths)"
                      placeholder="버킷 경로 입력 후 Enter (또는 포커스 벗어나면 자동 추가)"
                      helperText="원본 파일이 저장된 GCS 버킷 경로들 (여러 버킷에서 파일 검색 시 모두 입력)"
                      onBlur={(e) => {
                        // Enter 없이 포커스가 벗어날 때 잔여 텍스트를 자동으로 chip 으로 추가.
                        // Enter-only 입력 UX 가 헷갈려 저장이 누락되는 문제 방지.
                        const pending = (e.target.value || '').trim();
                        if (!pending) return;
                        const current = formData.original_bucket_paths || [];
                        if (current.includes(pending)) return;
                        const next = [...current, pending];
                        setFormData(prev => ({
                          ...prev,
                          original_bucket_paths: next,
                          original_bucket_path: next[0]
                        }));
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="변환 파일 버킷 경로 (converted_bucket_path)"
                  name="converted_bucket_path"
                  value={formData.converted_bucket_path || ''}
                  onChange={handleChange}
                  placeholder="예: gs://hyperlounge-data/customer/converted/"
                  helperText="변환된 파일이 저장될 GCS 버킷 경로"
                />
              </Grid>

            </Grid>
          )}

          {/* Tab 1: 프롬프트별 데이터 정보 (N:N 관계) */}
          {tabValue === 1 && (
            <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* 연결된 프롬프트 목록 */}
              <Box>
                <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                  연결된 프롬프트 ({linkedPrompts.length}개)
                </Typography>

                {linkedPrompts.length === 0 ? (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    이 파일에 연결된 프롬프트가 없습니다. 아래에서 프롬프트를 추가하세요.
                  </Alert>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
                    {linkedPrompts.map((prompt) => (
                      <Accordion key={prompt.prompt_id}>
                        <AccordionSummary
                          expandIcon={<ExpandMoreIcon />}
                          sx={{
                            '&:hover': { bgcolor: 'action.hover' },
                            borderLeft: 3,
                            borderColor: 'primary.main'
                          }}
                        >
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', pr: 2 }}>
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="h6" fontWeight="medium">
                                {prompt.prompt_id}
                              </Typography>
                              {prompt.prompt_name && (
                                <Typography variant="caption" color="text.secondary">
                                  {prompt.prompt_name}
                                </Typography>
                              )}
                            </Box>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <Tooltip title="프롬프트 편집">
                                <IconButton
                                  color="primary"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleEditPrompt(prompt);
                                  }}
                                  size="small"
                                >
                                  <EditIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="연결 해제">
                                <IconButton
                                  color="error"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleRemovePrompt(prompt.prompt_id);
                                  }}
                                  size="small"
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {prompt.convert_prompt && (
                              <Box>
                                <Typography variant="subtitle2" color="primary" gutterBottom>
                                  변환 프롬프트
                                </Typography>
                                <Paper
                                  elevation={0}
                                  sx={{
                                    p: 2,
                                    bgcolor: 'grey.50',
                                    border: '1px solid',
                                    borderColor: 'grey.300',
                                    borderRadius: 1,
                                    maxHeight: 300,
                                    overflow: 'auto'
                                  }}
                                >
                                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                    {prompt.convert_prompt}
                                  </Typography>
                                </Paper>
                              </Box>
                            )}
                            {prompt.current_prompt && (
                              <Box>
                                <Typography variant="subtitle2" color="primary" gutterBottom>
                                  현재 프롬프트
                                </Typography>
                                <Paper
                                  elevation={0}
                                  sx={{
                                    p: 2,
                                    bgcolor: 'grey.50',
                                    border: '1px solid',
                                    borderColor: 'grey.300',
                                    borderRadius: 1,
                                    maxHeight: 300,
                                    overflow: 'auto'
                                  }}
                                >
                                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                    {prompt.current_prompt}
                                  </Typography>
                                </Paper>
                              </Box>
                            )}
                            {(prompt.reference_sheet_names || prompt.exclude_sheet_keywords) && (
                              <Box>
                                <Typography variant="subtitle2" color="primary" gutterBottom>
                                  시트 필터링 설정
                                </Typography>
                                <Paper
                                  elevation={0}
                                  sx={{
                                    p: 2,
                                    bgcolor: 'grey.50',
                                    border: '1px solid',
                                    borderColor: 'grey.300',
                                    borderRadius: 1
                                  }}
                                >
                                  {prompt.reference_sheet_names && (
                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                      <strong>참조 시트명:</strong> {Array.isArray(prompt.reference_sheet_names) ? prompt.reference_sheet_names.join(', ') : prompt.reference_sheet_names}
                                    </Typography>
                                  )}
                                  {prompt.exclude_sheet_keywords && (
                                    <Typography variant="body2">
                                      <strong>제외 키워드:</strong> {Array.isArray(prompt.exclude_sheet_keywords) ? prompt.exclude_sheet_keywords.join(', ') : prompt.exclude_sheet_keywords}
                                    </Typography>
                                  )}
                                </Paper>
                              </Box>
                            )}
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <Chip
                                label={prompt.junction_is_active ? '활성' : '비활성'}
                                color={prompt.junction_is_active ? 'success' : 'default'}
                                size="small"
                              />
                              {prompt.created_at && (
                                <Chip
                                  label={`생성: ${new Date(prompt.created_at).toLocaleDateString('ko-KR')}`}
                                  size="small"
                                  variant="outlined"
                                />
                              )}
                            </Box>
                          </Box>
                        </AccordionDetails>
                      </Accordion>
                    ))}
                  </Box>
                )}
              </Box>

              {/* 프롬프트 추가 섹션 */}
              <Box sx={{ borderTop: 1, borderColor: 'divider', pt: 2 }}>
                <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                  프롬프트 추가
                </Typography>
                <Autocomplete
                  options={[
                    { prompt_id: '__NEW__', isNew: true },
                    ...prompts.filter(p => !linkedPrompts.some(lp => lp.prompt_id === p.prompt_id))
                  ]}
                  getOptionLabel={(option) => {
                    if (option.isNew) return '➕ 새 프롬프트 만들기';
                    const fileCount = option.file_count || 0;
                    return `${option.prompt_id} (${fileCount}개 파일에서 사용 중)`;
                  }}
                  onChange={handleAddPrompt}
                  value={null}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="프롬프트 선택"
                      placeholder="기존 프롬프트 선택 또는 새로 만들기"
                      helperText="이 파일에 프롬프트를 추가합니다. 한 파일에 여러 프롬프트를 연결할 수 있습니다."
                    />
                  )}
                  renderOption={(props, option) => (
                    <Box component="li" {...props} key={option.prompt_id}>
                      {option.isNew ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'primary.main' }}>
                          <AddIcon fontSize="small" />
                          <Typography>새 프롬프트 만들기</Typography>
                        </Box>
                      ) : (
                        <Box>
                          <Typography variant="body1" fontWeight="medium">
                            {option.prompt_id}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {option.file_count || 0}개 파일에서 사용 중
                            {option.convert_prompt && ' • 데이터 변환 프롬프트 있음'}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  )}
                  noOptionsText="추가 가능한 프롬프트가 없습니다"
                  isOptionEqualToValue={(option, value) => option.prompt_id === value?.prompt_id}
                />

                {/* 새 프롬프트 생성 폼 (isCreatingNewPrompt이거나 selectedPrompt가 null이고 formData.prompt_id가 있을 때 표시) */}
                {(isCreatingNewPrompt || (!selectedPrompt && formData.prompt_id)) && (
                  <Box sx={{ mt: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                    <Typography variant="subtitle2" color="primary" gutterBottom>
                      새 프롬프트 생성
                    </Typography>

                    <TextField
                      label="프롬프트 ID *"
                      name="prompt_id"
                      value={formData.prompt_id}
                      onChange={handleChange}
                      fullWidth
                      required
                      placeholder="프롬프트 식별자 (예: default, v2_monthly)"
                      helperText="여러 파일이 공유할 프롬프트의 고유 ID"
                      sx={{ mb: 2 }}
                    />

                    <TextField
                      label="프롬프트 이름 *"
                      name="prompt_name"
                      value={formData.prompt_name}
                      onChange={handleChange}
                      fullWidth
                      required
                      placeholder="프롬프트 설명 (예: 기본 변환 프롬프트)"
                      helperText="사용자가 이해하기 쉬운 프롬프트 이름"
                      sx={{ mb: 2 }}
                    />

                    <TextField
                      label="변환 프롬프트"
                      name="convert_prompt"
                      value={formData.convert_prompt}
                      onChange={handleChange}
                      fullWidth
                      multiline
                      rows={4}
                      placeholder="변환 프롬프트 내용..."
                      sx={{ mb: 2 }}
                    />

                    <TextField
                      label="현재 프롬프트"
                      name="current_prompt"
                      value={formData.current_prompt}
                      onChange={handleChange}
                      fullWidth
                      multiline
                      rows={6}
                      placeholder="현재 사용 중인 프롬프트 내용..."
                      sx={{ mb: 2 }}
                    />

                    <TextField
                      label="참조 시트명 (OR 조건)"
                      name="referenceSheetNames"
                      value={formData.referenceSheetNames || ''}
                      onChange={handleChange}
                      fullWidth
                      placeholder="매출, 실적, Summary (쉼표로 구분)"
                      helperText="변환에 포함할 시트명 리스트 (쉼표로 구분). 시트명에 이 단어가 포함되면 OR 조건으로 포함됩니다. 비워두면 모든 시트 포함."
                      sx={{ mb: 2 }}
                    />

                    <TextField
                      label="제외 시트 키워드"
                      name="excludeSheetKeywords"
                      value={formData.excludeSheetKeywords || ''}
                      onChange={handleChange}
                      fullWidth
                      placeholder="임시, temp, test (쉼표로 구분)"
                      helperText="시트명에 이 키워드가 포함되면 제외합니다 (쉼표로 구분)"
                      sx={{ mb: 2 }}
                    />
                  </Box>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>취소</Button>
          <Button onClick={handleSave} variant="contained">
            저장
          </Button>
        </DialogActions>
      </Dialog>

      {/* 프롬프트 편집 다이얼로그 */}
      <Dialog open={editDialogOpen} onClose={handleCloseEditDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          프롬프트 편집: {editingPrompt?.prompt_id}
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          {editingPrompt && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              <TextField
                label="프롬프트 ID (읽기 전용)"
                value={editingPrompt.prompt_id || ''}
                disabled
                fullWidth
                helperText="프롬프트 ID는 기본 키로 수정할 수 없습니다. 변경이 필요하면 새 프롬프트를 생성해주세요."
              />

              <TextField
                label="프롬프트 이름"
                value={editingPrompt.prompt_name || ''}
                onChange={(e) => setEditingPrompt(prev => ({ ...prev, prompt_name: e.target.value }))}
                fullWidth
                placeholder="프롬프트 설명 (예: 기본 변환 프롬프트)"
              />

              <TextField
                label="참조 시트명 (OR 조건)"
                value={editingPrompt.referenceSheetNames || ''}
                onChange={(e) => setEditingPrompt(prev => ({ ...prev, referenceSheetNames: e.target.value }))}
                fullWidth
                placeholder="매출, 실적, Summary (쉼표로 구분)"
                helperText="변환에 포함할 시트명 리스트 (쉼표로 구분). 시트명에 이 단어가 포함되면 OR 조건으로 포함됩니다. 비워두면 모든 시트 포함."
              />

              <TextField
                label="제외 시트 키워드"
                value={editingPrompt.excludeSheetKeywords || ''}
                onChange={(e) => setEditingPrompt(prev => ({ ...prev, excludeSheetKeywords: e.target.value }))}
                fullWidth
                placeholder="임시, temp, test (쉼표로 구분)"
                helperText="시트명에 이 키워드가 포함되면 제외합니다 (쉼표로 구분)"
              />

              <TextField
                label="변환 프롬프트"
                value={editingPrompt.convert_prompt || ''}
                onChange={(e) => setEditingPrompt(prev => ({ ...prev, convert_prompt: e.target.value }))}
                fullWidth
                multiline
                rows={6}
                placeholder="변환 프롬프트 내용..."
              />

              <TextField
                label="현재 프롬프트"
                value={editingPrompt.current_prompt || ''}
                onChange={(e) => setEditingPrompt(prev => ({ ...prev, current_prompt: e.target.value }))}
                fullWidth
                multiline
                rows={8}
                placeholder="현재 사용 중인 프롬프트 내용..."
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={editingPrompt.is_active !== false}
                    onChange={(e) => setEditingPrompt(prev => ({ ...prev, is_active: e.target.checked }))}
                  />
                }
                label="활성 상태"
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditDialog}>취소</Button>
          <Button onClick={handleSaveEditedPrompt} variant="contained" color="primary">
            저장
          </Button>
        </DialogActions>
      </Dialog>

      {/* 삭제 확인 다이얼로그 */}
      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        fileToDelete={fileToDelete}
        onConfirm={handleDeleteConfirm}
      />
    </Container>
  );
};

export default OntologyPage;
